import { Field, TAnswer } from "src/shared/contexts/HackerContext/types";
import {
  isEmail,
  isNonNegative,
  isPhoneNumber,
  notBlank,
  isDate,
} from "src/shared/utils/validation";

import {
  ACCEPTED_FIELDS,
  RECIPIENT_DETAIL_FIELDS,
  VALIDATORS as BASE_VALIDATORS,
} from "./constants";

export const isRapidContactInformation = (value: TAnswer) => {
  if (typeof value !== "string" || value.trim().length === 0) return false;

  const contactInformation = value.trim();
  return isPhoneNumber(contactInformation) || isEmail(contactInformation);
};

// Centralized error messages to mirror existing onChange copy
export const ERROR_MESSAGES: Partial<Record<Field | "followup", string>> = {
  [Field.TRAVEL_PHONE_NUMBER]:
    "Please enter a valid phone number or email address.",
  [Field.FRIDAY_FLIGHT_AIRLINE]: "Please enter the airline name.",
  [Field.FRIDAY_FLIGHT_NUMBER]: "Please enter a flight number.",
  [Field.FRIDAY_FLIGHT_ARRIVAL_TIME]: "Please enter a valid arrival time.",
  [Field.ARRIVAL_AIRPORT]: "Please enter a 3-letter airport code.",

  [Field.SUNDAY_FLIGHT_AIRLINE]: "Please enter the airline name.",
  [Field.SUNDAY_FLIGHT_NUMBER]: "Please enter a flight number.",
  [Field.SUNDAY_FLIGHT_DEPARTURE_TIME]: "Please enter a valid departure time.",
  [Field.DEPARTURE_AIRPORT]: "Please enter a 3-letter airport code.",

  [Field.FLIGHT_CURRENCY]: "Please select a valid currency.",
  [Field.FLIGHT_AMOUNT]: "Please enter a valid amount.",

  [Field.REIMBURSEMENT_METHOD]: "Please select a reimbursement method.",
  [Field.REIMBURSEMENT_ACKNOWLEDGEMENT]:
    "Please acknowledge the reimbursement policy.",

  [Field.VISA_TRACKING]: "Please enter your visa tracking number.",

  // Follow-up (PayPal / e-Transfer) message depends on which field is active
  followup: "This field is required.",
};

// Extend validators used throughout the form to cover dynamic fields
const EXTENDED_VALIDATORS: Partial<
  Record<Field, (value: TAnswer, ctx?: any) => boolean>
> = {
  ...BASE_VALIDATORS,
  [Field.TRAVEL_PHONE_NUMBER]: isRapidContactInformation,
  // Visa tracking only applies if requires visa
  [Field.VISA_TRACKING]: (value: TAnswer, ctx?: any) =>
    ctx?.requiresVisa ? notBlank(value) : true,
  // Flight amount should be non-negative (existing copy uses "valid amount")
  [Field.FLIGHT_AMOUNT]: (value: TAnswer) => isNonNegative(value),
  // Reimbursement recipient details
  [Field.REIMBURSEMENT_RECIPIENT_PAYPAL]: (value: TAnswer) => notBlank(value),
  [Field.REIMBURSEMENT_RECIPIENT_ETRANSFER]: (value: TAnswer) =>
    notBlank(value),
  [Field.REIMBURSEMENT_RECIPIENT_NAME]: (value: TAnswer) => notBlank(value),
  // Date fields (fallback, in case consumer doesn't use BASE_VALIDATORS' isDate)
  [Field.FRIDAY_FLIGHT_ARRIVAL_TIME]: (value: TAnswer) => isDate(value),
  [Field.SUNDAY_FLIGHT_DEPARTURE_TIME]: (value: TAnswer) => isDate(value),
};

export type ValidateOptions = {
  arrivalOrDeparture?: "arrival" | "departure"; // for time field message wording
  requiresVisa?: boolean;
  reimbursementMethod?: string;
};

export const getFieldError = (
  field: Field | "followup",
  value: TAnswer,
  opts: ValidateOptions = {}
): string => {
  // Special case for followup: pick message based on actual field intent
  if (field === "followup") {
    // Caller should prefer supplying the specific field (PAYPAL/ETRANSFER) directly
    return ERROR_MESSAGES.followup || "This field is required";
  }

  const validator = EXTENDED_VALIDATORS[field];
  let valid = true;
  if (validator) {
    valid = validator(value, opts);
  }

  if (valid) return "";

  // Wording for time fields depends on arrival/departure
  if (
    field === Field.FRIDAY_FLIGHT_ARRIVAL_TIME &&
    opts.arrivalOrDeparture === "arrival"
  ) {
    return ERROR_MESSAGES[Field.FRIDAY_FLIGHT_ARRIVAL_TIME] as string;
  }
  if (
    field === Field.SUNDAY_FLIGHT_DEPARTURE_TIME &&
    opts.arrivalOrDeparture === "departure"
  ) {
    return ERROR_MESSAGES[Field.SUNDAY_FLIGHT_DEPARTURE_TIME] as string;
  }

  return (ERROR_MESSAGES[field as Field] as string) || "This field is required";
};

// Compute the follow-up error (PayPal/e-Transfer) based on selected method
export const computeFollowupError = (
  responses: Record<string, any>
): string => {
  const method = responses[Field.REIMBURSEMENT_METHOD];
  if (method === "paypal") {
    return (
      getFieldError(
        Field.REIMBURSEMENT_RECIPIENT_PAYPAL,
        responses[Field.REIMBURSEMENT_RECIPIENT_PAYPAL]
      ) || ""
    );
  }
  if (method === "cadETransfer") {
    return (
      getFieldError(
        Field.REIMBURSEMENT_RECIPIENT_ETRANSFER,
        responses[Field.REIMBURSEMENT_RECIPIENT_ETRANSFER]
      ) || ""
    );
  }
  return ""; // no follow-up until a method is chosen
};

// Validate the entire form and return an errors object matching consumer expectations
export const validateAll = (
  responses: Record<string, any>
): Record<string, string> => {
  const errors: Record<string, string> = {};

  ACCEPTED_FIELDS.forEach((field) => {
    const errorKey = RECIPIENT_DETAIL_FIELDS.includes(field)
      ? "followup"
      : field;

    // Options for dynamic validators and message wording
    const opts: ValidateOptions = {
      requiresVisa: !!responses[Field.REQUIRES_VISA],
      reimbursementMethod: responses[Field.REIMBURSEMENT_METHOD] as string,
      arrivalOrDeparture:
        field === Field.FRIDAY_FLIGHT_ARRIVAL_TIME
          ? "arrival"
          : field === Field.SUNDAY_FLIGHT_DEPARTURE_TIME
          ? "departure"
          : undefined,
    };

    if (errorKey === "followup") {
      // Only compute followup for the field that corresponds to the selected method
      const msg = computeFollowupError(responses);
      errors.followup = msg;
    } else {
      const msg = getFieldError(field, responses[field], opts);
      errors[errorKey] = msg;
    }
  });

  return errors;
};
